import {
  UpdateEventType,
  type UpdateActivityKind,
  type UpdateActivityRequest,
} from '@electron/shared/update.types';
import { isChatStatusBusy } from '@littlebird/sdk';
import { comparer, reaction } from 'mobx';

import {
  isMeetingPostProcessingActive,
  isMeetingRecordingActive,
} from '@/features/meetings/meeting-activity';
import { isSpeechActivityActive } from '@/features/speech-input/speech-activity-handoff';
import { isTranscriptionPending } from '@/features/speech-input/transcription-activity';
import { littlebirdSdk } from '@/lib/sdk-init';
import { reportUpdateActivity } from '@/lib/update-activity-ipc';
import { listenWithAbortIPC } from '@/utils/abort';
import { inElectron } from '@/utils/capabilities';

function isAnyChatBusy(): boolean {
  return Array.from(littlebirdSdk.stores.threads.data.values()).some((thread) =>
    isChatStatusBusy(thread.status)
  );
}

function findActiveKinds(): UpdateActivityKind[] {
  const active: UpdateActivityKind[] = [];
  if (isAnyChatBusy()) active.push('chat');
  if (littlebirdSdk.voiceMode.session !== null) active.push('voice');
  if (isSpeechActivityActive()) active.push('speech');
  if (isTranscriptionPending()) active.push('transcription');
  // Spans meeting startup through stopping; the main process only hears about
  // a meeting once the native recording has started.
  if (isMeetingRecordingActive()) active.push('meeting');
  if (isMeetingPostProcessingActive()) active.push('postProcessing');
  return active;
}

export function startUpdateActivityReporter(): () => void {
  if (!inElectron()) return () => {};

  function report(requestGeneration?: number): void {
    reportUpdateActivity({ activeKinds: findActiveKinds(), requestGeneration });
  }

  const stopReacting = reaction(findActiveKinds, () => report(), {
    equals: comparer.structural,
    fireImmediately: true,
  });

  // The main process asks before installing an update, so a report lost to a
  // startup race cannot leave it deciding on stale state. The generation goes
  // back untouched, letting it tell this answer apart from an unrelated push.
  const stopListening = listenWithAbortIPC<UpdateActivityRequest>(
    UpdateEventType.RequestActivityReport,
    (request) => report(request.generation)
  );

  return () => {
    stopReacting();
    stopListening?.();
  };
}
